import { Button, Drawer, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { Add, ArrowBack, BorderColor, ChatBubble, Check, Close } from '@mui/icons-material'
import React, { useEffect, useMemo, useState } from 'react'
import {
  StyledContainerColumn,
  StyledContainerColumnLeft,
  StyledContainerRowBetween,
} from '../../../styles/ContainerStyles'
import { Box } from '@mui/system'
import { SquareButton } from '../../../styles/ButtonStyles'
import { LoadingButton } from '@mui/lab'
import CommentCard from './CommentCard'
import TextInput from '../../Form/Inputs/TextInput'
import { useForm } from 'react-hook-form'
import { CommentFormValues, defaultCommentFormValues } from '../../../store/types/form/CommentFormValues'
import {
  useCreateCommentMutation,
  useDeleteCommentMutation,
  useGetCommentsQuery,
  useUpdateCommentMutation,
} from '../../../store/redux/reducers/commentSlice'
import TargetType from '../../../store/types/enums/TargetType'
import { createErrorMessage } from '../../../store/utils'
import { useNotification } from '../../../store/redux/hooks'
import CustomComment from '../../../store/types/models/CustomComment'
import { useSelector } from 'react-redux'
import { getCurrentUser } from '../../../store/redux/reducers/currentUserSlice'
import ConfirmationDialog from '../../Dialog/ConfirmationDialog'
import DiscardChangesDialog from '../../Dialog/DiscardChangesDialog'
import { useTranslation } from 'react-i18next'

type CommentsSidebarProps = {
  open: boolean
  setOpen: (open: boolean) => void
  commentedCandidate: { id: string; firstName: string; lastName: string; localization: string } | undefined
  setCommentsCount?: React.Dispatch<React.SetStateAction<number>>
}

const CommentsSidebar = ({ open, setOpen, commentedCandidate, setCommentsCount }: CommentsSidebarProps) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const { displayNotification } = useNotification()
  const currentUser = useSelector(getCurrentUser)
  const { data } = useGetCommentsQuery(
    {
      page: 0,
      size: 20,
      targetType: TargetType.CANDIDATE,
      targetId: commentedCandidate?.id,
    },
    { skip: !commentedCandidate?.id }
  )
  const [createComment] = useCreateCommentMutation()
  const [updateComment] = useUpdateCommentMutation()
  const [deleteComment] = useDeleteCommentMutation()

  useEffect(() => {
    if (data && setCommentsCount) {
      setCommentsCount(data.totalElements)
    }
  }, [data, setCommentsCount])

  const handleClose = () => {
    if (isChangePresent) setIsCancelDialogOpen(true)
    else {
      handleResetForm()
      setOpen(false)
    }
  }

  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState<boolean>(false)
  const [isCancelFormButtonClick, setIsCancelFormButtonClick] = useState<boolean>(false)
  const [isChangePresent, setIsChangePresent] = useState<boolean>(false)
  const [isViewForm, setIsViewForm] = useState<boolean>(false)
  const [isNewComment, setIsNewComment] = useState<boolean>(false)
  const [editedCommentId, setEditedCommentId] = useState<string>('')

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<CommentFormValues>({
    defaultValues: defaultCommentFormValues,
  })

  useEffect(() => {
    setIsChangePresent(isDirty)
  }, [isDirty, setIsChangePresent])

  useEffect(() => {
    if (isCancelFormButtonClick) setIsCancelDialogOpen(true)
  }, [isCancelFormButtonClick])

  const handleDialogReset = () => {
    if (!isCancelFormButtonClick) setOpen(false)
    handleResetForm()
  }

  const handleResetForm = () => {
    reset(defaultCommentFormValues)
    setIsNewComment(false)
    setIsViewForm(false)
  }

  const handleAddButtonClick = () => {
    setIsNewComment(true)
    setIsViewForm(true)
  }

  const handleEditButtonClick = (comment: CustomComment) => {
    setIsNewComment(false)
    setIsViewForm(true)
    setEditedCommentId(comment.id)
    reset({ content: comment.content })
  }

  const handleCancelButtonClick = () => {
    if (isDirty) {
      setIsCancelFormButtonClick(true)
    } else handleResetForm()
  }

  const handleCreate = async (data: CommentFormValues) => {
    try {
      await createComment({ ...data, targetId: commentedCandidate?.id, targetType: TargetType.CANDIDATE }).unwrap()
      displayNotification({ message: t('components.commentsSidebar.form.commentAdded'), type: 'success' })
      handleResetForm()
    } catch (error) {
      let errorDescription = createErrorMessage(error)
      displayNotification({ message: `${t('page.users.message.error')} ${errorDescription}`, type: 'error' })
    }
  }

  const handleEdit = async (data: CommentFormValues) => {
    if (!editedCommentId) {
      return
    }

    try {
      await updateComment({ id: editedCommentId, body: { content: data.content } }).unwrap()
      displayNotification({ message: t('components.commentsSidebar.form.commentEdited'), type: 'success' })
      handleResetForm()
    } catch (error) {
      let errorDescription = createErrorMessage(error)
      displayNotification({ message: `${t('page.users.message.error')} ${errorDescription}`, type: 'error' })
    }
  }

  const handleSaveButtonClick = () => {
    handleSubmit(data => (isNewComment ? handleCreate(data) : handleEdit(data)))()
  }

  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false)
  const [deleteCommentId, setDeleteCommentId] = useState<string>('')
  const handleDeleteButtonClick = (comment: CustomComment) => {
    setIsConfirmationDialogOpen(true)
    setDeleteCommentId(comment.id)
  }

  const handleConfirmDelete = async () => {
    if (!deleteCommentId) {
      return
    }

    try {
      await deleteComment(deleteCommentId).unwrap()
      setDeleteCommentId('')
      displayNotification({ message: t('components.commentsSidebar.form.commentDeleted'), type: 'success' })
      setIsConfirmationDialogOpen(false)
    } catch (error) {
      let errorDescription = createErrorMessage(error)
      displayNotification({ message: `${t('page.users.message.error')} ${errorDescription}`, type: 'error' })
    }
  }

  const sidebarTitle = useMemo(() => {
    if (!isViewForm) return t('components.commentsSidebar.title')
    if (isNewComment) return t('components.commentsSidebar.newComment')
    return t('components.commentsSidebar.editComment')
  }, [isViewForm, isNewComment, t])

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { bgcolor: 'background.main', overflow: 'hidden' }, elevation: 0 }}
      >
        <Stack px={isDownSm ? 2.5 : 5.5} py={6} width={isDownSm ? '350px' : '570px'} height={'100%'}>
          <Button
            onClick={handleClose}
            variant="text"
            color="secondary"
            startIcon={<ArrowBack />}
            size="small"
            sx={{ width: 'fit-content', ml: -3 }}
            aria-label={t('components.infoSidebar.return')}
          >
            {t('components.infoSidebar.return')}
          </Button>
          <Stack sx={{ gap: 4, pt: 6, height: '100%' }}>
            <StyledContainerRowBetween sx={{ flexDirection: isDownSm ? 'column' : 'row', px: 0.5 }}>
              <Box
                sx={{
                  gap: 2,
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  width: isDownSm ? '100%' : 'auto',
                }}
              >
                {!isViewForm ? <ChatBubble sx={{ fontSize: 36 }} /> : <BorderColor sx={{ fontSize: 36 }} />}
                <StyledContainerColumnLeft>
                  <Typography variant="h5" component="h1" color={'text.highEmphasis'}>
                    {sidebarTitle}
                  </Typography>
                  {commentedCandidate && (
                    <Typography
                      color={'text.highEmphasis'}
                      variant={'subtitle1'}
                    >{`${commentedCandidate.firstName} ${commentedCandidate.lastName} • ${commentedCandidate.localization}`}</Typography>
                  )}
                </StyledContainerColumnLeft>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, width: isDownSm ? '100%' : 'auto' }}>
                {isViewForm && (
                  <SquareButton
                    onClick={handleCancelButtonClick}
                    color="secondary"
                    disabled={false}
                    aria-label={t('components.commentsSidebar.buttonCancel')}
                  >
                    <Close />
                  </SquareButton>
                )}
                <LoadingButton
                  startIcon={!isViewForm ? <Add /> : <Check />}
                  variant="contained"
                  size="medium"
                  disabled={false}
                  loading={false}
                  fullWidth={isDownSm}
                  aria-label={
                    !isViewForm ? t('components.commentsSidebar.buttonAdd') : t('components.commentsSidebar.buttonSave')
                  }
                  onClick={!isViewForm ? handleAddButtonClick : handleSaveButtonClick}
                >
                  {!isViewForm ? t(`components.commentsSidebar.buttonAdd`) : t(`components.commentsSidebar.buttonSave`)}
                </LoadingButton>
              </Box>
            </StyledContainerRowBetween>
            {!isViewForm ? (
              <>
                {data?.content.length === 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="h6">{t('components.commentsSidebar.noComments')}</Typography>
                  </Box>
                ) : (
                  <StyledContainerColumn sx={{ gap: 2, height: 'auto', overflowY: 'auto', p: 0.5 }}>
                    {data?.content.map(comment => (
                      <CommentCard
                        key={comment.id}
                        handleEdit={() => handleEditButtonClick(comment)}
                        comment={comment}
                        currentUserId={currentUser?.id}
                        handleDelete={() => handleDeleteButtonClick(comment)}
                      />
                    ))}
                  </StyledContainerColumn>
                )}
              </>
            ) : (
              <StyledContainerColumn sx={{ gap: 2, px: 0.5 }}>
                <TextInput
                  name={'content'}
                  control={control}
                  label={t('components.commentsSidebar.form.commentContent')}
                  multiline
                  rows={9}
                  showCharCounter
                  maxLength={500}
                  rules={{
                    maxLength: {
                      value: 500,
                      message: t('components.commentsSidebar.form.commentContentMax'),
                    },
                    required: t('components.commentsSidebar.form.commentContentRequired'),
                  }}
                />
              </StyledContainerColumn>
            )}
          </Stack>
        </Stack>
        <ConfirmationDialog
          open={isConfirmationDialogOpen}
          setOpen={setIsConfirmationDialogOpen}
          handleConfirm={handleConfirmDelete}
          title={t('components.commentsSidebar.form.deleteCommentConfirmTitle')}
          content={t('components.commentsSidebar.form.deleteCommentConfirmContent')}
        />
      </Drawer>
      <DiscardChangesDialog
        open={isCancelDialogOpen}
        checkUnsavedChanges={() => true}
        setIsCancelDialogOpen={setIsCancelDialogOpen}
        disableBlocker={true}
        resetForm={handleDialogReset}
        actionOnClose={() => setIsCancelFormButtonClick(false)}
      />
    </>
  )
}

export default CommentsSidebar
